import { Controller } from "@hotwired/stimulus";

// Calculates the best discount for a ticket based on quantity
// Compares early bird, group discount, and promo code discounts
export default class extends Controller {
  static targets = [
    "discountBadge",
    "originalPrice",
    "discountedPrice",
    "savingsAmount",
    "discountInfo"
  ];

  static values = {
    ticketId: Number,
    ticketPrice: Number,
    earlyBirdEnabled: Boolean,
    earlyBirdData: Object, // { id, price, ends_at }
    groupPricingEnabled: Boolean,
    groupDiscounts: Array, // [{ id, quantity, discount_type, value }]
    promoCode: Object, // { id, name, discount_type, amount, applicable }
  };

  connect() {
    // Initialize discount calculation
    this.currentQuantity = 0;
    this.currentDiscount = { type: 'none', amount: 0, id: null };
  }

  // Calculate best discount for given quantity
  calculateBestDiscount(quantity) {
    this.currentQuantity = quantity;
    const discounts = [];

    // 1. Calculate Early Bird Discount
    if (this.earlyBirdEnabledValue && this.hasEarlyBirdDataValue) {
      const earlyBirdDiscount = this.calculateEarlyBirdDiscount();
      if (earlyBirdDiscount.amount > 0) discounts.push(earlyBirdDiscount)
    }

    // 2. Calculate Group Discount
    if (this.groupPricingEnabledValue && this.hasGroupDiscountsValue) {
      const groupDiscount = this.calculateGroupDiscount(quantity);
      if (groupDiscount.amount > 0) discounts.push(groupDiscount)
    }

    // 3. Calculate Promo Code Discount
    if (this.hasPromoCodeValue && this.promoCodeValue.applicable) {
      const promoDiscount = this.calculatePromoDiscount();
      if (promoDiscount.amount > 0) discounts.push(promoDiscount)
    }

    // Find the discount with highest savings
    const bestDiscount = discounts.length > 0
      ? discounts.reduce((best, current) => current.totalDiscount > best.totalDiscount ? current : best)
      : { type: 'none', amount: 0, totalDiscount: 0, id: null, finalPrice: this.ticketPriceValue };

    this.currentDiscount = bestDiscount;
    this.updateUI(bestDiscount);
    
    return bestDiscount;
  }

  calculateEarlyBirdDiscount() {
    const earlyBird = this.earlyBirdDataValue;
    
    // Check if early bird is still active
    if (!this.isEarlyBirdActive(earlyBird)) {
      return { type: 'none', amount: 0, totalDiscount: 0 };
    }

    // Early bird sets the FINAL price during the window, so the discount the
    // buyer receives is the SAVINGS (regular price - early bird price), not the
    // early bird price itself. Clamp the final price to [0, ticketPrice] and the
    // savings to >= 0 so an early bird priced at/above the ticket yields a 0
    // discount instead of a negative/inflated one. Mirrors the server-side
    // TicketDiscountCalculator#calculate_early_bird_discount.
    const finalPrice = Math.min(Math.max(earlyBird.price, 0), this.ticketPriceValue);
    const discountAmount = Math.max(this.ticketPriceValue - finalPrice, 0);

    return {
      type: 'early_bird',
      id: earlyBird.id,
      amount: discountAmount,
      totalDiscount: discountAmount * this.currentQuantity,
      finalPrice: finalPrice,
      metadata: {
        early_bird_price: earlyBird.price,
        ends_at: earlyBird.ends_at
      }
    };
  }

  calculateGroupDiscount(quantity) {
    if (!this.groupDiscountsValue || this.groupDiscountsValue.length === 0) {
      return { type: 'none', amount: 0, totalDiscount: 0 };
    }

    // Find applicable group discounts (where quantity meets minimum)
    const applicableDiscounts = this.groupDiscountsValue.filter(
      gd => quantity >= gd.quantity
    );

    if (applicableDiscounts.length === 0) {
      return { type: 'none', amount: 0, totalDiscount: 0 };
    }

    // Get the one with highest quantity requirement (most specific tier)
    const bestGroupDiscount = applicableDiscounts.reduce((best, current) =>
      current.quantity > best.quantity ? current : best
    );

    // Calculate discount amount
    let discountAmount;
    if (bestGroupDiscount.discount_type === 'dollar') {
      discountAmount = bestGroupDiscount.value;
    } else {
      // percent
      discountAmount = (this.ticketPriceValue * bestGroupDiscount.value) / 100;
    }

    // Ensure discount doesn't exceed ticket price
    discountAmount = Math.min(discountAmount, this.ticketPriceValue);

    return {
      type: 'group_discount',
      id: bestGroupDiscount.id,
      amount: discountAmount,
      totalDiscount: discountAmount * quantity,
      finalPrice: this.ticketPriceValue - discountAmount,
      metadata: {
        tier_quantity: bestGroupDiscount.quantity,
        discount_type: bestGroupDiscount.discount_type,
        discount_value: bestGroupDiscount.value
      }
    };
  }

  calculatePromoDiscount() {
    const promo = this.promoCodeValue;
    
    let discountAmount;
    if (promo.discount_type === 'dollar') {
      discountAmount = promo.amount;
    } else {
      // percent
      discountAmount = (this.ticketPriceValue * promo.amount) / 100;
    }

    // Ensure discount doesn't exceed ticket price
    discountAmount = Math.min(discountAmount, this.ticketPriceValue);

    return {
      type: 'promo_code',
      id: promo.id,
      amount: discountAmount,
      totalDiscount: discountAmount * this.currentQuantity,
      finalPrice: this.ticketPriceValue - discountAmount,
      metadata: {
        promo_code_name: promo.name,
        discount_type: promo.discount_type,
        discount_amount: promo.amount
      }
    };
  }

  isEarlyBirdActive(earlyBird) {
    if (!earlyBird || !earlyBird.ends_at) {
      return false;
    }

    // Simple comparison with UTC timestamp (ISO8601 format)
    const now = new Date();
    const endsAt = new Date(earlyBird.ends_at);

    return now <= endsAt;
  }

  updateUI(discount) {
    // Update discount badge

    if (this.hasDiscountBadgeTarget) {
      if (discount.type !== 'none' && discount.totalDiscount > 0) {
        this.discountBadgeTarget.textContent = this.getDiscountLabel(discount);
        this.discountBadgeTarget.classList.remove('d-none');
      } else {
        this.discountBadgeTarget.classList.add('d-none');
      }
    }

    // Update prices
    if (this.hasOriginalPriceTarget && discount.type !== 'none') {
      this.originalPriceTarget.classList.add('strikethrough');
    } else if (this.hasOriginalPriceTarget) {
      this.originalPriceTarget.classList.remove('strikethrough');
    }

    if (this.hasDiscountedPriceTarget) {
      if (discount.type !== 'none') {
        this.discountedPriceTarget.textContent = `$${discount.finalPrice.toFixed(2)}`;
        this.discountedPriceTarget.classList.remove('d-none');
      } else {
        this.discountedPriceTarget.classList.add('d-none');
      }
    }

    // Update savings amount
    if (this.hasSavingsAmountTarget && discount.type !== 'none'  && discount.totalDiscount > 0) {
      this.savingsAmountTarget.textContent = `Save $${discount.totalDiscount.toFixed(2)}`;
      this.savingsAmountTarget.classList.remove('d-none');
    } else if (this.hasSavingsAmountTarget) {
      this.savingsAmountTarget.classList.add('d-none');
    }
  }

  getDiscountLabel(discount) {
    switch (discount.type) {
      case 'early_bird':
        return 'Early Bird';
      case 'group_discount':
        return 'Group Discount';
      case 'promo_code':
        return 'Promo Code';
      default:
        return '';
    }
  }

  // Public method to get current discount info
  getCurrentDiscount() {
    return this.currentDiscount;
  }
}

